import { useMemo } from 'react';

/**
 * Builds a hierarchy tree from a flat array of options.
 * Each option must have a unique `value` and can optionally include a `parentId`.
 */
export const buildHierarchy = (options = []) => {
  const nodesById = {};

  options.forEach((opt) => {
    if (opt == null || typeof opt.value === 'undefined') {
      return;
    }

    const clone = {
      ...opt,
      parentId: opt.parentId ?? null,
    };

    nodesById[clone.value] = {
      id: clone.value,
      option: clone,
      parent: undefined,
      children: [],
    };
  });

  const roots = [];

  Object.values(nodesById).forEach((node) => {
    const parentId = node.option.parentId;
    if (parentId != null && typeof nodesById[parentId] !== 'undefined') {
      node.parent = nodesById[parentId];
      nodesById[parentId].children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items) => {
    items.sort((a, b) =>
      String(a.option.label ?? '').localeCompare(String(b.option.label ?? '')),
    );
    items.forEach((child) => sortNodes(child.children));
  };

  sortNodes(roots);

  const assignMeta = (node, ancestors) => {
    const ancestorIds = ancestors.map((ancestor) => ancestor.id);
    const ancestorLabels = ancestors.map((ancestor) => ancestor.option.label ?? '');

    node.option.ancestorIds = ancestorIds;
    node.option.pathLabel = [...ancestorLabels, node.option.label ?? ''];
    node.option.fullLabel = node.option.pathLabel.join(' › ');
    node.option.depth = ancestors.length;
    node.children.forEach((child) => assignMeta(child, [...ancestors, node]));
  };

  roots.forEach((root) => assignMeta(root, []));

  return { roots, nodesById };
};

/**
 * Enhances a flat list of options with hierarchy metadata and returns both the options
 * and the generated hierarchy tree.
 */
export const enhanceHierarchyOptions = (options = []) => {
  const { roots, nodesById } = buildHierarchy(options);
  const enhancedOptions = Object.values(nodesById).map((node) => node.option);
  return { options: enhancedOptions, tree: { roots, nodesById } };
};

/**
 * Flattens the hierarchy tree into an ordered array of options suitable for rendering in a dropdown.
 * Only nodes whose parents are present in the `expanded` set will have their children included.
 */
export const flattenHierarchy = (nodes = [], expanded = new Set(), depth = 0) => {
  const result = [];

  nodes.forEach((node) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.id);

    result.push({
      ...node.option,
      depth,
      isParent: hasChildren,
      isExpanded,
    });

    if (hasChildren && isExpanded) {
      result.push(...flattenHierarchy(node.children, expanded, depth + 1));
    }
  });

  return result;
};

/**
 * Returns a Set containing all node ids that have at least one child.
 */
export const getExpandableNodeIds = (nodesById = {}) => {
  const ids = new Set();
  Object.values(nodesById).forEach((node) => {
    if (node.children.length > 0) {
      ids.add(node.id);
    }
  });
  return ids;
};

/**
 * Returns the full label (State › Customer › Site) for the provided node id if available.
 */
export const getHierarchyFullLabel = (nodesById = {}, id) => {
  const node = typeof id === 'undefined' || id === null ? undefined : nodesById[id];
  return node?.option?.fullLabel ?? node?.option?.label ?? '';
};

/**
 * Returns the ancestor ids for a given node id, ordered from root to the immediate parent.
 */
export const getHierarchyAncestorIds = (nodesById = {}, id) => {
  const node = typeof id === 'undefined' || id === null ? undefined : nodesById[id];
  return node?.option?.ancestorIds ?? [];
};

/**
 * React helper hook that memoises enhanced hierarchy options.
 */
export const useHierarchyOptions = (options = []) =>
  useMemo(() => enhanceHierarchyOptions(options), [options]);

